/* eslint-disable no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable default-param-last */

import debounce from 'lodash.debounce';
import { createSlice } from '@reduxjs/toolkit';

// Media queries
const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
const reducedMotionQuery = window.matchMedia(
  '(prefers-reduced-motion: reduce)',
);
const portraitView = window.matchMedia('(orientation: portrait)');
const landscapeView = window.matchMedia('(orientation: landscape)');

// Initial state
const init = {
  width: window.innerWidth || 0,
  height: window.innerHeight || 0,
  devicePixelRatio: window.devicePixelRatio || 1,
  darkMode: colorSchemeQuery.matches,
  reducedMotion: reducedMotionQuery.matches,
  online: navigator.onLine,
  visibility: document.visibilityState,
  orientation: portraitView.matches
    ? 'portrait'
    : landscapeView.matches
      ? 'landscape'
      : '',
};

// Slice
const browserUtils = createSlice({
  name: 'browserUtils',
  initialState: init,
  reducers: {
    onResize: (state, action) => {
      const { width, height, devicePixelRatio } = action.payload || {};
      if (typeof width === 'number') state.width = width;
      if (typeof height === 'number') state.height = height;
      if (typeof devicePixelRatio === 'number') {
        state.devicePixelRatio = devicePixelRatio;
      }
    },
    onDarkMode: (state, action) => {
      state.darkMode = Boolean(action.payload?.bool);
    },
    onReducedMotion: (state, action) => {
      state.reducedMotion = Boolean(action.payload?.bool);
    },
    onOnline: (state, action) => {
      state.online = Boolean(action.payload?.bool);
    },
    onVisibilityChange: (state, action) => {
      state.visibility = action.payload?.visibility || 'hidden';
    },
    onOrientationChange: (state, action) => {
      state.orientation = action.payload?.orientation || '';
    },
  },
});

export default browserUtils;

// Actions
const {
  onResize,
  onDarkMode,
  onReducedMotion,
  onOnline,
  onVisibilityChange,
  onOrientationChange,
} = browserUtils.actions;

/**
 * Set up browser listeners and dispatch updates into Redux.
 *
 * Usage:
 *   import store from './store';
 *   import { setUpBrowserListeners } from './slices/browserUtils';
 *
 *   const cleanup = setUpBrowserListeners(store.dispatch);
 */
export function setUpBrowserListeners(dispatch) {
  if (typeof dispatch !== 'function') {
    throw new Error(
      'setUpBrowserListeners(dispatch) requires a Redux dispatch function',
    );
  }

  // Resize (debounced)
  const handleResize = debounce(() => {
    dispatch(
      onResize({
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1,
      }),
    );
  }, 200);

  // Dark mode
  const handleDarkMode = (e) => {
    dispatch(onDarkMode({ bool: e.matches }));
  };

  // Reduced motion preference
  const handleReducedMotion = (e) => {
    dispatch(onReducedMotion({ bool: e.matches }));
  };

  // Network status
  const handleOnline = () => {
    dispatch(onOnline({ bool: true }));
  };

  const handleOffline = () => {
    dispatch(onOnline({ bool: false }));
  };

  // Page visibility
  const handleVisibilityChange = () => {
    dispatch(onVisibilityChange({ visibility: document.visibilityState }));
  };

  // Orientation
  const handlePortrait = (e) => {
    if (e.matches) {
      dispatch(onOrientationChange({ orientation: 'portrait' }));
    }
  };

  const handleLandscape = (e) => {
    if (e.matches) {
      dispatch(onOrientationChange({ orientation: 'landscape' }));
    }
  };

  // Attach listeners
  window.addEventListener('resize', handleResize);
  colorSchemeQuery?.addEventListener('change', handleDarkMode);
  reducedMotionQuery?.addEventListener('change', handleReducedMotion);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  portraitView?.addEventListener('change', handlePortrait);
  landscapeView?.addEventListener('change', handleLandscape);

  // Initial dispatch
  dispatch(
    onResize({
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    }),
  );

  dispatch(onReducedMotion({ bool: reducedMotionQuery.matches }));
  dispatch(onOnline({ bool: navigator.onLine }));
  dispatch(onVisibilityChange({ visibility: document.visibilityState }));

  if (portraitView.matches) {
    dispatch(onOrientationChange({ orientation: 'portrait' }));
  } else if (landscapeView.matches) {
    dispatch(onOrientationChange({ orientation: 'landscape' }));
  }

  // Cleanup (important for HMR / teardown)
  return () => {
    window.removeEventListener('resize', handleResize);
    handleResize.cancel?.();

    colorSchemeQuery?.removeEventListener('change', handleDarkMode);
    reducedMotionQuery?.removeEventListener('change', handleReducedMotion);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    portraitView?.removeEventListener('change', handlePortrait);
    landscapeView?.removeEventListener('change', handleLandscape);
  };
}
